import React, { useEffect } from 'react';
import { Col, Container, Row, Image } from 'react-bootstrap';
import styles from './AboutUs.style';
import TitleBar from '../../Components/TitleBar/TitleBar';
import TitleImage from '../../assets/images/about-us.png';
import Section from '../../Components/Section/Section';
import { useTranslation } from 'react-i18next';
import {
  faBullseye,
  faLightbulb,
  faUserTie,
  faShoppingCart,
  faHandshake,
  faRocket
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';


const AboutUs: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbs: string[] = [t("footer.home"), t("footer.aboutUs")];
  const title: string = 'About Us';
  const info: string = "";
  const imageUrl: string = TitleImage;


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  // Data for feature cards
  const features = [
    {
      icon: faBullseye,
      title: "Our Mission",
      description: "We are transforming the retail and service landscape by offering a platform that emphasizes convenience, quality, and customer satisfaction. Our goal is to simplify your life by providing a comprehensive range of products and services."
    },
    {
      icon: faShoppingCart,
      title: "Convenient Shopping",
      description: "Purchase your favorite products from your preferred or nearest shop. Choose pickup or delivery - convenience that suits you best."
    },
    {
      icon: faUserTie,
      title: "Empowering Local Retail",
      description: "We are dedicated to supporting and empowering local retailers by helping them reach a broader audience and thrive in a competitive market."
    },
    {
      icon: faHandshake,
      title: "Local Community Focus",
      description: "Sajilo Life serves as a bridge between local retailers and buyers, creating a community where local businesses flourish."
    }
  ];


  const values = [
    { icon: faUserTie, title: "Customer-Centric", description: "Your satisfaction is our highest priority" },
    { icon: faLightbulb, title: "Innovation", description: "We continuously improve and leverage latest technologies" },
    { icon: faBullseye, title: "Integrity", description: "We operate with transparency and honesty" }
  ];


  const offerings = [
    { title: "Daily Essentials", description: "Groceries and household items" },
    { title: "Food Delivery", description: "Your favorite meals delivered promptly" },
    { title: "Fresh Vegetables", description: "Fresh produce for healthy living" },
    { title: "Fashion", description: "Latest trends in clothing & accessories" },
    { title: "Electronics", description: "Latest gadgets & devices" },
    { title: "Hardware", description: "Tools & supplies for all projects" },
    { title: "Home Services", description: "Repairs, plumbing, wiring & more" }
  ];


  const futureServices = [
    "Invoice and POS - IRD-compliant billing solutions",
    "Direct Payment Link - Secure payment processing",
    "E-commerce Platform - Custom domain & delivery management",
    "Buy Now, Pay Later - Flexible payment options",
    "Credit to Retail Buyers - Financial convenience",
    "Ride Sharing - Easy commute solutions",
    "Micro Lending - Financial support partnerships",
    "Classifieds - Buy and sell within community",
    "Property Rental Management - Streamlined rental process"
  ];


  return (
    <div className="about-us common-page">
      <TitleBar breadcrumbs={breadcrumbs} title={title} info={info} imageUrl={imageUrl} />
     
      {/* Hero Section - Company Introduction */}
      <Section className="aboutUs" style={styles.heroSection}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} md={12} className="mb-4 mb-lg-0">
              <div style={styles.heroContent}>
                <h2 style={styles.heroTitle}>Welcome to Sajilo Life</h2>
                <p style={styles.heroSubtitle}>
                  Where our mission is clear, <span>Makes Life Easier.</span>
                </p>
                <p style={styles.heroDescription}>
                  As a leading retail-first marketplace, we are committed to streamlining your shopping and service needs, delivering everything you require directly to your doorstep.
                </p>
              </div>
            </Col>
            <Col lg={6} md={12}>
              <div style={styles.heroImageWrapper}>
                {/* <Image
                  // src={TitleImage}
                  alt="Sajilo Life"
                  style={styles.heroImage}
                  fluid
                /> */}
              </div>
            </Col>
          </Row>
        </Container>
      </Section>


      {/* Features Section */}
      <Section className="features-section" style={styles.featuresSection}>
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <h2 style={styles.sectionTitle}>What We Do</h2>
            </Col>
          </Row>
          <Row>
            {features.map((feature, index) => (
              <Col lg={3} md={6} sm={12} key={index} className="mb-4">
                <div style={styles.featureCard}>
                  <div style={styles.featureIconWrapper}>
                    <FontAwesomeIcon icon={feature.icon} style={styles.featureIcon} />
                  </div>
                  <h3 style={styles.featureTitle}>{feature.title}</h3>
                  <p style={styles.featureText}>{feature.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </Section>


      {/* Our Offerings */}
      <Section className="offerings-section" style={styles.offeringsSection}>
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <h2 style={styles.sectionTitle}>Our Offerings</h2>
              <p style={styles.sectionSubtitle}>
                A comprehensive range of products and services tailored for you
              </p>
            </Col>
          </Row>
          <Row>
            {offerings.map((offering, index) => (
              <Col lg={4} md={6} sm={12} key={index} className="mb-4">
                <div style={styles.offeringCard}>
                  <div style={styles.offeringDot}></div>
                  <h4 style={styles.offeringTitle}>{offering.title}</h4>
                  <p style={styles.offeringDescription}>{offering.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </Section>


      {/* Core Values */}
      <Section className="values-section" style={styles.valuesSection}>
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <h2 style={styles.sectionTitle}>Our Core Values</h2>
            </Col>
          </Row>
          <Row>
            {values.map((value, index) => (
              <Col lg={4} md={6} sm={12} key={index} className="mb-4">
                <div style={styles.valueCard}>
                  <div style={styles.valueIconWrapper}>
                    <FontAwesomeIcon icon={value.icon} style={styles.valueIcon} />
                  </div>
                  <h4 style={styles.valueTitle}>{value.title}</h4>
                  <p style={styles.valueText}>{value.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </Section>


      {/* Future Services */}
      <Section className="future-services-section" style={styles.futureSection}>
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <h2 style={styles.sectionTitle}>Exciting Services Coming Soon</h2>
              <p style={styles.sectionSubtitle}>
                We are constantly evolving to meet your needs
              </p>
            </Col>
          </Row>
          <Row>
            {futureServices.map((service, index) => (
              <Col lg={6} md={6} sm={12} key={index} className="mb-3">
                <div style={styles.futureServiceItem}>
                  <span style={styles.serviceBullet}>✓</span>
                  <span style={styles.serviceText}>{service}</span>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </Section>


      {/* Profit Focus Section */}
      <Section className="profit-section" style={styles.profitSection}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} md={12} className="mb-4 mb-lg-0">
              <h2 style={styles.sectionTitleLeft}>Maximizing Seller Profits</h2>
              <p style={styles.profitText}>
                We focus on maintaining <span>low commission rates</span> on sales, ensuring that our sellers can enjoy <span>maximum profits</span> from their sales. This approach allows local businesses to thrive and reinvest in their growth, benefiting the entire community.
              </p>
            </Col>
            <Col lg={6} md={12}>
              <div style={styles.profitCard}>
                <div style={styles.profitIconWrapper}>
                  <FontAwesomeIcon icon={faRocket} style={styles.profitIcon} />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </Section>


      {/* Call to Action Section */}
      <Section className="cta-section" style={styles.ctaSection}>
        <Container>
          <Row>
            <Col lg={12} className="text-center">
              <h2 style={styles.ctaTitle}>Join Us on Our Journey</h2>
              <p style={styles.ctaText}>
                At <span>Sajilo Life</span>, we are more than just a marketplace, we are a community. Whether you're shopping for essentials, ordering food, or booking home services, Sajilo Life is here to make your life easier.
              </p>
              <p style={styles.ctaClosing}>
                Thank you for choosing Sajilo Life. Together, let's make life simpler and more enjoyable.
              </p>
            </Col>
          </Row>
        </Container>
      </Section>
    </div>
  );
};


export default AboutUs;
       

